import { useMemo, useRef, useState, type ReactNode } from 'react';
import { pickLocale, type Characteristic } from '../../schema';
import { useTranslation } from '../../i18n';
import { useComponentSettings } from '../../state/ComponentContext';
import { useValidationErrorsFor } from '../../state/StoreContext';
import { InfoIcon } from '../InfoIcon';
import { InfoPopover } from '../InfoPopover';
import { HtmlContent } from '../HtmlContent';

interface CharacteristicShellProps {
    characteristic: Characteristic;
    locale: string | undefined;
    children: ReactNode;
    /**
     * A custom renderer serving several Characteristics (`combine`): their
     * ids, this one first. Errors of all of them show here, and the ids go
     * on the wrapper so "scroll to the first error" finds the widget.
     */
    combinedIds?: readonly number[] | undefined;
    /** Only the wrapper with its attributes — a custom renderer with `replace: 'all'` draws the rest. */
    bare?: boolean;
}

/**
 * Common wrapper for every Characteristic view.
 *
 * Layout: the Characteristic image (when present) sits next to the title
 * and info HTML in a head row; the body (input / values) always spans the
 * full container width below. Putting the body next to the image would
 * squeeze radio rows / listimage tiles into a narrow column on desktop
 * (see admin feedback after 18e463a5 went live) — full-width is the
 * sensible default for every view type. `valueImagePosition` still flips
 * the image between the left and right edge of the head row; `donotshow`
 * hides it entirely.
 */
export function CharacteristicShell({ characteristic, locale, children, combinedIds, bare = false }: CharacteristicShellProps) {
    const { t } = useTranslation();
    const settings = useComponentSettings();
    const errorIds = useMemo(() => combinedIds ?? [characteristic.id], [combinedIds, characteristic.id]);
    const validationErrors = useValidationErrorsFor(errorIds);
    // No title → no heading, as in legacy. Falling back to the internal
    // name put slugs like `hinweis_montage` above static text blocks.
    const title = pickLocale(characteristic.title, locale) ?? '';
    const info = pickLocale(characteristic.info, locale);
    const moreInfo = pickLocale(characteristic.moreInfo, locale);
    const required = 'isRequired' in characteristic && characteristic.isRequired;

    // Characteristic-level moreInfo uses the same portal-anchored InfoPopover
    // as ListImageView value-info, so the two "ⓘ" widgets behave identically.
    const [infoOpen, setInfoOpen] = useState(false);
    const moreInfoTriggerRef = useRef<HTMLButtonElement | null>(null);
    const showImage =
        Boolean(characteristic.optionImage) && settings.valueImagePosition !== 'donotshow';
    const imagePosition: 'left' | 'right' =
        settings.valueImagePosition === 'right' ? 'right' : 'left';

    const image = showImage ? (
        <img
            className="cfg-char__option-image"
            src={characteristic.optionImage}
            alt={title || characteristic.altTitle}
            loading="lazy"
            data-configento-position={imagePosition}
        />
    ) : null;

    // Admin-authored CSS classes ride alongside the framework's own
    // class list, same as legacy `option/abstract.js:758` appends
    // `getCustomClass()` to the outer wrapper. Merchants use this for
    // grid sizing (`grid12-6`, `quartersize`), summary-hiding
    // (`summary-hide-price`), and ad-hoc CSS hooks the storefront
    // theme styles against.
    // Several class names here are a documented theming contract, see
    // tests/stableClasses.test.tsx before renaming any.
    const wrapperClassName = ['cfg-char', (characteristic.cssClass ?? '').trim()]
        .filter((s) => s !== '')
        .join(' ');

    return (
        <div
            className={wrapperClassName}
            data-configento-char-id={characteristic.id}
            data-configento-char-type={characteristic.type}
            data-configento-image-position={showImage ? imagePosition : undefined}
            data-configento-invalid={validationErrors.length > 0 || undefined}
            data-configento-combined={combinedIds && combinedIds.length > 1 ? combinedIds.join(' ') : undefined}
        >
            {bare ? children : <>
            {(title || required || info || moreInfo || showImage) && (
            <div className="cfg-char__head-row">
                {showImage && imagePosition === 'left' && image}
                <div className="cfg-char__head-content">
                    {(title || required || moreInfo) && (
                    <div className="cfg-char__head">
                        <span className="cfg-char__title">
                            {title}
                            {required && (
                                <span className="cfg-char__required" aria-label={t('characteristic.required_aria')}>
                                    *
                                </span>
                            )}
                        </span>
                        {moreInfo && (
                            <button
                                ref={moreInfoTriggerRef}
                                type="button"
                                className="cfg-info-trigger cfg-char__more-info-icon"
                                aria-label={t('characteristic.more_info_aria')}
                                aria-expanded={infoOpen}
                                onClick={() => setInfoOpen((v) => !v)}
                            >
                                <InfoIcon />
                            </button>
                        )}
                    </div>
                    )}
                    {info && (
                        <HtmlContent className="cfg-char__info" html={info} />
                    )}
                </div>
                {showImage && imagePosition === 'right' && image}
            </div>
            )}
            <div className="cfg-char__body">{children}</div>
            {validationErrors.length > 0 && (
                <ul className="cfg-char__errors" role="alert">
                    {validationErrors.map((err, idx) => (
                        <li key={`${err.code}-${idx}`} className="cfg-char__error">
                            {err.message}
                        </li>
                    ))}
                </ul>
            )}
            {moreInfo && infoOpen && (
                <InfoPopover
                    html={moreInfo}
                    title={title}
                    triggerEl={moreInfoTriggerRef.current}
                    onClose={() => setInfoOpen(false)}
                />
            )}
            </>}
        </div>
    );
}
